/* -------------------------------------------------------------------------- */
/*                                  Packages                                  */
/* -------------------------------------------------------------------------- */
import FunnelStepScreen from "@components/prequalify/FunnelStepScreen"
import { graphql, useStaticQuery } from "gatsby"
import * as React from "react"
import { useWindowWidth } from "@react-hook/window-size"
import { SEO } from "abmg-components"
import { useForm } from "react-hook-form"
import { renderToString } from "react-dom/server"
import { Check } from "react-feather"

/* -------------------------------------------------------------------------- */
/*                             External Components                            */
/* -------------------------------------------------------------------------- */
import {
  requiredOptionRule,
  requiredRule,
} from "@components/global/UnderlineField"
import Modal from "@components/global/Modal"
import ConsentPopup from "@components/global/funnels/ConsentPopup"

/* -------------------------------------------------------------------------- */
/*                               External Hooks                               */
/* -------------------------------------------------------------------------- */
import useClientSideOnly from "@hooks/use-client-side-only"

/* -------------------------------------------------------------------------- */
/*                               External Utils                               */
/* -------------------------------------------------------------------------- */
import { bankersInMBSearch, sortAlphabetically } from "@utils/mb"
import { citiesByState, getBreakpoints } from "@utils/prequalify"

/* -------------------------------------------------------------------------- */
/*                                External Data                               */
/* -------------------------------------------------------------------------- */
import formLeadRecipients from "@data/pre-qualify-form-lead-recipients.json"

/* -------------------------------------------------------------------------- */
/*                                  Variables                                 */
/* -------------------------------------------------------------------------- */
export const storedFunnelName = `purchase-prequalify-funnel`

/* -------------------------------------------------------------------------- */
/*                              Default Component                             */
/* -------------------------------------------------------------------------- */
export default function PurchasePrequalify(props) {
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [funnelData, setFunnelData] = React.useState({
    currentStep: 0,
    formData: {},
    passedSteps: {},
  })

  const updateFunnelData = React.useCallback(
    (updatedFunnelData, save) => {
      const newFunnelData = {
        formData: updatedFunnelData.formData,
        currentStep:
          updatedFunnelData.currentStep !== undefined
            ? updatedFunnelData.currentStep
            : funnelData.currentStep,
        passedSteps: updatedFunnelData.passedSteps,
      }
      if (save)
        localStorage.setItem(storedFunnelName, JSON.stringify(newFunnelData))
      setFunnelData(newFunnelData)
    },
    [funnelData],
  )

  const isMounted = useClientSideOnly()
  const windowWidth = useWindowWidth()
  const breakpoints = getBreakpoints({ windowWidth, isMounted })

  const {
    clearErrors,
    control,
    handleSubmit,
    register,
    setValue,
    getValues,
    trigger,
    formState: { errors, isValid },
    watch,
    reset,
    resetField,
    setFocus,
  } = useForm({
    mode: "onTouched",
    defaultValues: funnelData?.formData,
  })

  const reactHookFormProps = {
    controller: {
      control,
    },
    clearErrors,
    handleSubmit,
    errors,
    register,
    setValue,
    getValues,
    reset,
    resetField,
    setFocus,
  }

  React.useEffect(() => {
    const storedFunnelData = JSON.parse(localStorage.getItem(storedFunnelName))
    if (storedFunnelData) {
      setFunnelData(storedFunnelData)
      reset(storedFunnelData.formData)
    }
  }, [])

  const data = useStaticQuery(graphql`
    query {
      HelloThere: file(
        relativePath: { regex: "/pre-qualify/purchase-funnel/hello-there/" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      DreamHomeLoading: file(
        relativePath: {
          regex: "/pre-qualify/purchase-funnel/dream-home-loading/"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      NewLivingStandard: file(
        relativePath: {
          regex: "/pre-qualify/purchase-funnel/new-living-standard/"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      BigMovesStartSmall: file(
        relativePath: {
          regex: "/pre-qualify/purchase-funnel/big-moves-start-small/"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      AddingUpTheNumbers: file(
        relativePath: {
          regex: "/pre-qualify/purchase-funnel/adding-up-the-numbers/"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      HitYourTarget: file(
        relativePath: { regex: "/pre-qualify/purchase-funnel/hit-your-target/" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      PointUsHome: file(
        relativePath: { regex: "/pre-qualify/purchase-funnel/point-us-home/" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      GotBackup: file(
        relativePath: { regex: "/pre-qualify/purchase-funnel/got-backup/" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      PeopleFirstAlways: file(
        relativePath: {
          regex: "/pre-qualify/purchase-funnel/people-first-always/"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
      RingRing: file(
        relativePath: { regex: "/pre-qualify/purchase-funnel/ring-ring/" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED)
        }
      }
    }
  `)
  const classes = {
    contact: {
      formElement: {
        default: `font-variant-numeric--primary bg-transparent border-b border-grey-8 leading-[35px] [outline:0] text-[17px] text-grey-8 -tracking-[.33px] focus:border-b-pacific-blue `,
        error: `border-sunset-orange-6`,
      },
      label: {
        default: `font-variant-numeric--primary leading-[12px] text-grey-8`,
      },
    },
    radio: {
      fieldsContainer: {
        default: `flex flex-col h-full justify-center`,
      },
      formElement: {
        default: `appearance-none bg-center border border-grey-4 box-border h-[29px]  p-0 rounded-full w-[29px] group-focus:[&:not(:checked)]:border-grey-8 group-hover:[&:not(:checked)]:border-grey-8 checked:before:content-none checked:border-grey-4  checked:!bg-pacific-blue-2 checked:bg-[image:var(--backgroundImage)]`,
      },
      formElementAllContainer: {
        default: ` h-full `,
      },
      formElementGroup: {
        default: `${
          breakpoints.minTablet ? "h-[65px]" : ""
        } group bg-white border border-grey4 flex gap-[12px] items-center [padding:34px_30px] rounded-[13px] w-full [&:has(:checked)]:bg-atlantic-blue-2 focus-within:bg-atlantic-blue-2 hover:bg-atlantic-blue-2`,
      },
      formElementGroupsContainer: {
        default: `grid ${"content-center grid-auto-rows-[minmax(65px,85px)] gap-[22px] h-full"}  `,
      },
      form: {
        default: `[--formHeight:413px] min-h-[var(--formHeight)] items-center  w-full   [&:not(:has(input[type=radio]))]:bg-white [&:not(:has(input[type=radio]))]:border [&:not(:has(input[type=radio]))]:border-grey-4  ${
          breakpoints.minTablet
            ? "[&:not(:has(input[type=radio]))]:p-[58px]"
            : "[&:not(:has(input[type=radio]))]:[padding:71px_30px]"
        }`,
      },
      label: {
        default: `caption font-variant-numeric--primary leading-[27px] text-[19px] text-left text-grey-7 -tracking-[.38px]`,
      },
    },
    range: {
      form: {
        default: `bg-white border border-grey-4 flex flex-col h-full justify-center ${
          breakpoints.minTablet ? "p-[58px]" : "[padding:71px_30px]"
        } `,
      },
      label: {
        default: `leading-[20px] text-[15px] text-[#0C1112]`,
      },
      minMaxLabel: {
        default: `font-normal eading-[normal] text-[12px] text-[#0C1112]`,
      },
      undecidedLabel: {
        default: `font-variant-numeric--primary leading-[15px] text-[15px] text-grey-6`,
      },
    },
  }

  const tooltipAttributes = {
    inputGroup: { placement: "right" },
  }

  const undecidedText = `Not sure about this one? Just check this box and hit 'Next'.`
  const rangeLabelText = `Price Range`

  const radioButtonOnClick = e => {
    const input = e.target.querySelector("input")

    if (input)
      setValue(input.name, input.value, {
        shouldValidate: true,
        shouldTouch: true,
      })
  }
  const radioInputStyle = {
    "--backgroundImage": `url(data:image/svg+xml,${encodeURIComponent(
      renderToString(<Check color={"#fff"} width={20} height={20} />),
    )})`,
  }

  const radioInputAttributes = {
    style: radioInputStyle,
  }

  const radioInputClasses = {
    classes: {
      default: classes.radio.formElement.default,
    },
  }

  const funnelSteps = [
    {
      formSide: {
        heading: {
          text: `Tell us a little about yourself`,
        },
        form: {
          classes: {
            default: `bg-white border border-grey-4 h-full  ${
              breakpoints.minTablet ? "p-[58px]" : "[padding:71px_30px]"
            } `,
          },
        },
        fieldsContainer: {
          classes: {
            default: `flex flex-col gap-[20px] max-w-[338px] w-full`,
          },
        },
        fields: [
          {
            formElement: {
              attributes: {
                name: "first_name",
                type: "text",
              },
              classes: {
                ...classes.contact.formElement,
              },
              type: "input",
              validationRules: {
                ...requiredRule,
              },
            },
            label: {
              classes: {
                ...classes.contact.label,
              },
              text: "First Name",
            },
          },
          {
            formElement: {
              attributes: {
                name: "last_name",
                type: "text",
              },
              classes: {
                ...classes.contact.formElement,
              },
              type: "input",
              validationRules: {
                ...requiredRule,
              },
            },
            label: {
              classes: {
                ...classes.contact.label,
              },
              text: "Last Name",
            },
          },
          {
            formElement: {
              attributes: {
                name: "email",
                type: "email",
              },
              classes: {
                ...classes.contact.formElement,
              },
              type: "input",
              validationRules: {
                ...requiredRule,
              },
            },
            label: {
              classes: {
                ...classes.contact.label,
              },
              text: "Email",
            },
          },
          {
            formElement: {
              attributes: {
                name: "phone_cell",
                type: "phone",
              },
              classes: {
                ...classes.contact.formElement,
              },

              type: "input",
              validationRules: {
                ...requiredRule,
              },
            },
            label: {
              classes: {
                ...classes.contact.label,
              },
              text: "Phone number",
            },
          },
          {
            formElement: {
              attributes: {
                name: "terms",
                type: "checkbox",
              },
              type: "input",
              validationRules: {
                required: {
                  value: true,
                  message: `Please read the terms and conditions and check the box to agree.`,
                },
              },
            },
            formElementGroup: {
              classes: {
                default: `bg-grey-1 [padding:8px_12px] w-fit`,
              },
            },
            label: {
              classes: {
                default: `text-grey-7`,
              },
              text: (
                <>
                  I have read and agree to the{" "}
                  <button
                    className="text-pacific-blue"
                    onClick={() => setIsModalOpen(true)}
                    type="button"
                  >
                    terms and conditions
                  </button>
                </>
              ),
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              Knowing the best way to reach you helps us offer the best possible
              guidance—tailored to your timeline, your questions, and your
              goals. We’re here to help, every step of the way.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData: data.HelloThere.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "contact_info",
    },
    {
      formSide: {
        heading: { text: `What kind of home are you looking for?` },
        fieldsContainer: {
          classes: {
            default: classes.radio.fieldsContainer.default,
          },
        },
        form: {
          classes: {
            default: classes.radio.form.default,
          },
        },
        fields: [
          {
            formElementAllContainer: {
              classes: {
                default: classes.radio.formElementAllContainer.default,
              },
            },
            formElementGroupsContainer: {
              classes: {
                default: classes.radio.formElementGroupsContainer.default,
              },
            },
            formElementGroup: {
              attributes: {
                onClick: radioButtonOnClick,
                type: "button",
              },
              classes: {
                default: classes.radio.formElementGroup.default,
              },
              tagName: "button",
            },
            inputGroup: {
              fields: [
                {
                  formElement: {
                    attributes: {
                      id: "single_family_home",
                      value: "single-family home",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Single-Family Home",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "townhouse",
                      value: "townhouse",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Townhouse",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "condo",
                      value: "condo",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Condo",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "multi_family_home",
                      value: "multi-family home",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Multi-Family Home",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "mobile_manufactured",
                      value: "mobile/manufactured",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Mobile/Manufactured",
                  },
                },
              ],
              name: "home_type",
              type: "radio",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            tooltipAttributes: {
              ...tooltipAttributes.inputGroup,
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              Every home tells a story, so let’s find one that fits yours!
              Whether you're drawn to the ease of a condo or the space a family
              home offers, your preferences help us guide you toward the right
              fit.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData:
            data.DreamHomeLoading.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "home_type",
    },
    {
      formSide: {
        heading: {
          text: `What best describes your current homebuying situation?`,
        },
        form: {
          classes: {
            default: classes.radio.form.default,
          },
        },
        fieldsContainer: {
          classes: {
            default: classes.radio.fieldsContainer.default,
          },
        },
        fields: [
          {
            formElementAllContainer: {
              classes: {
                default: classes.radio.formElementAllContainer.default,
              },
            },
            formElementGroupsContainer: {
              classes: {
                default: classes.radio.formElementGroupsContainer.default,
              },
            },
            formElementGroup: {
              attributes: {
                onClick: radioButtonOnClick,
                type: "button",
              },
              classes: {
                default: classes.radio.formElementGroup.default,
              },
              tagName: "button",
            },
            inputGroup: {
              fields: [
                {
                  formElement: {
                    attributes: {
                      id: "actively_looking",
                      value: "actively_looking",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "I’m actively looking",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "found_home",
                      value: "found_home",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "I think I found the home I want to purchase",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "thinking_about_buying",
                      value: "thinking_about_buying",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "I’m just beginning to think about buying",
                  },
                },
              ],
              name: "purchase_timeline",
              type: "radio",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            tooltipAttributes: {
              ...tooltipAttributes.inputGroup,
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              Already browsing listings? Smart. Just testing the waters? No
              worries! We’re ready to meet you where you are and walk you
              through those all-important next steps with confidence.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData:
            data.NewLivingStandard.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "purchase_timeline",
    },
    {
      formSide: {
        heading: { text: `Will this be your first home purchase?` },
        form: {
          classes: {
            default: classes.radio.form.default,
          },
        },
        fieldsContainer: {
          classes: {
            default: classes.radio.fieldsContainer.default,
          },
        },
        fields: [
          {
            formElementAllContainer: {
              classes: {
                default: classes.radio.formElementAllContainer.default,
              },
            },
            formElementGroup: {
              attributes: {
                onClick: radioButtonOnClick,
                type: "button",
              },
              classes: {
                default: classes.radio.formElementGroup.default,
              },
              tagName: "button",
            },
            formElementGroupsContainer: {
              classes: {
                default: classes.radio.formElementGroupsContainer.default,
              },
            },
            inputGroup: {
              fields: [
                {
                  formElement: {
                    attributes: {
                      id: "first_time_home_buyer_yes",
                      value: "1",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Yes",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "first_time_home_buyer_no",
                      value: "0",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "No",
                  },
                },
              ],
              name: "first_time_home_buyer",
              type: "radio",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            tooltipAttributes: {
              ...tooltipAttributes.inputGroup,
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              If you’re a first-time buyer, you’re not alone—and you’re not in
              this alone, either. Understanding your experience level lets us
              personalize guidance and remove any guesswork from the upcoming
              process.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData:
            data.BigMovesStartSmall.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "first_time_home_buyer",
    },
    {
      formSide: {
        heading: { text: `Do you have a price range in mind?` },
        form: {
          classes: {
            default: classes.range.form.default,
          },
        },
        fieldsContainer: {
          classes: {
            default: ``,
          },
        },

        fields: [
          {
            formElement: {
              attributes: {
                max: 3000000,
                min: 75000,
                name: "estimated_purchase_price",
                step: 10000,
                type: "range",
              },
              defaultValue: [250000, 500000],
              type: "input",
            },
            label: {
              classes: {
                default: classes.range.label.default,
              },
              text: rangeLabelText,
            },
            minMaxLabel: {
              classes: {
                default: classes.range.minMaxLabel.default,
              },
            },
            undecidedLabel: {
              classes: {
                default: classes.range.undecidedLabel.default,
              },
              text: undecidedText,
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              Having a ballpark number allows us to provide realistic options
              that match your goals and financial comfort zone. It’s all part of
              making your path to homeownership smooth, stress- free, and
              successful.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData:
            data.AddingUpTheNumbers.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "estimated_purchase_price",
    },
    {
      formSide: {
        heading: { text: `What would you like your payment to be?` },
        form: {
          classes: {
            default: classes.range.form.default,
          },
        },
        fieldsContainer: {
          classes: {
            default: ``,
          },
        },
        fields: [
          {
            formElement: {
              attributes: {
                max: 10000,
                min: 800,
                name: "desired_monthly_payment",
                step: 100,
                type: "range",
              },
              defaultValue: [1000, 2000],
              type: "input",
            },
            label: {
              classes: {
                default: classes.range.label.default,
              },
              text: rangeLabelText,
            },
            minMaxLabel: {
              classes: {
                default: classes.range.minMaxLabel.default,
              },
            },
            undecidedLabel: {
              classes: {
                default: classes.range.undecidedLabel.default,
              },
              text: undecidedText,
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              What monthly payment feels right for you? We'll use this to find
              loan programs that align with your budget and keep you financially
              happy.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData: data.HitYourTarget.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "desired_monthly_payment",
    },
    {
      formSide: {
        heading: { text: `Where are you looking to buy?` },
        form: {
          classes: {
            default: classes.range.form.default,
          },
        },
        fieldsContainer: {
          classes: {
            default: ``,
          },
        },
        fields: [
          {
            formElement: {
              attributes: {
                name: "state_of_home_purchase",
                placeholder: "Select a state",
              },
              conditions: [
                {
                  values: Object.keys(citiesByState),
                  operator: "none",
                  matchCondition: "eq",
                  fieldName: "state_of_home_purchase",
                },
              ],
              options: Object.keys(formLeadRecipients).map(state => ({
                label: state,
                value: state,
              })),
              type: "select",
              validationRules: {
                ...requiredOptionRule,
              },
            },
          },
          {
            formElement: {
              attributes: {
                name: "city_of_home_purchase",
                placeholder: "Select the city closest to you",
              },
              conditions: [
                {
                  values: Object.keys(citiesByState),
                  operator: "or",
                  matchCondition: "eq",
                  fieldName: "state_of_home_purchase",
                  resetFieldsObject: {
                    city_of_home_purchase: null,
                  },
                },
              ],
              optionFieldName: "state_of_home_purchase",
              optionsByFieldValue: citiesByState,

              type: "select",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            resetButton: {
              resetFieldsObject: {
                state_of_home_purchase: null,
              },
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              The more we know about where you’re looking to live, the better we
              can tailor our support. From market trends to neighborhood feel,
              we’ll help match you with the right home in the right place.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData: data.PointUsHome.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "state_of_home_purchase",
    },
    {
      formSide: {
        heading: { text: `Are you working with a real estate agent? ` },
        form: {
          classes: {
            default: classes.radio.form.default,
          },
        },
        fieldsContainer: {
          classes: {
            default: classes.radio.fieldsContainer.default,
          },
        },
        fields: [
          {
            formElementAllContainer: {
              classes: {
                default: classes.radio.formElementAllContainer.default,
              },
            },

            formElementGroup: {
              attributes: {
                onClick: radioButtonOnClick,
                type: "button",
              },
              classes: {
                default: classes.radio.formElementGroup.default,
              },
              tagName: "button",
            },
            formElementGroupsContainer: {
              classes: {
                default: classes.radio.formElementGroupsContainer.default,
              },
            },
            inputGroup: {
              fields: [
                {
                  formElement: {
                    attributes: {
                      id: "has_agent_yes",
                      value: "1",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Yes",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "has_agent_no",
                      value: "0",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "No",
                  },
                },
              ],
              name: "has_agent",
              type: "radio",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            tooltipAttributes: {
              ...tooltipAttributes.inputGroup,
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              Let us know if you have a real estate agent on your team. We love
              working with pros to make your homebuying journey smooth and
              successful.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData: data.GotBackup.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "has_agent",
    },
    {
      formSide: {
        heading: {
          text: `Have you been in touch with an Atlantic Bay Mortgage Banker yet?`,
        },
        form: {
          classes: {
            default: classes.radio.form.default,
          },
        },
        fieldsContainer: {
          classes: {
            default: classes.radio.fieldsContainer.default,
          },
        },
        fields: [
          {
            formElementAllContainer: {
              classes: {
                default: classes.radio.formElementAllContainer.default,
              },
            },
            formElementGroup: {
              attributes: {
                onClick: radioButtonOnClick,
                type: "button",
              },
              classes: {
                default: classes.radio.formElementGroup.default,
              },
              tagName: "button",
            },
            formElementGroupsContainer: {
              classes: {
                default: classes.radio.formElementGroupsContainer.default,
              },
            },
            inputGroup: {
              conditions: [
                {
                  values: ["1"],
                  operator: "none",
                  matchCondition: "eq",
                  fieldName: "previous_abmg_client",
                },
              ],
              fields: [
                {
                  formElement: {
                    attributes: {
                      id: "previous_abmg_client_yes",
                      value: "1",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Yes",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "previous_abmg_client_no",
                      value: "0",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "No",
                  },
                },
              ],
              name: "previous_abmg_client",
              type: "radio",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            tooltipAttributes: {
              ...tooltipAttributes.inputGroup,
            },
          },
          {
            formElement: {
              attributes: {
                name: "mb_name",
                placeholder: `Type a name or anything you remember`,
              },
              conditions: [
                {
                  values: ["1"],
                  operator: "and",
                  matchCondition: "eq",
                  fieldName: "previous_abmg_client",
                  resetFieldsObject: {
                    mb_name: null,
                  },
                },
              ],

              options: sortAlphabetically(
                bankersInMBSearch.map(banker => ({
                  label: banker.name,
                  value: banker.slug,
                })),
                "label",
                "ASC",
              ),

              type: "datalist",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            helpElement: {
              text: (
                <>
                  Not sure of the name? Share anything you remember (e.g. first
                  name, where you met, or details that make them stand out){" "}
                </>
              ),
            },
            resetButton: {
              resetFieldsObject: {
                previous_abmg_client: null,
              },
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              Knowing if you’re already working with one of our Mortgage Bankers
              helps us serve you better. If you have someone, we’ll keep you
              connected. If not, we’ll match you with the right expert so you
              don’t miss a beat. It’s all about making sure your mortgage
              journey feels smooth and simple.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData:
            data.PeopleFirstAlways.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "previous_abmg_client",
    },
    {
      formSide: {
        heading: { text: `What’s your preferred method of contact?` },
        form: {
          classes: {
            default: classes.radio.form.default,
          },
        },
        fieldsContainer: {
          classes: {
            default: classes.radio.fieldsContainer.default,
          },
        },
        fields: [
          {
            formElementAllContainer: {
              classes: {
                default: classes.radio.formElementAllContainer.default,
              },
            },
            formElementGroup: {
              attributes: {
                onClick: radioButtonOnClick,
                type: "button",
              },
              classes: {
                default: classes.radio.formElementGroup.default,
              },
              tagName: "button",
            },
            formElementGroupsContainer: {
              classes: {
                default: classes.radio.formElementGroupsContainer.default,
              },
            },
            inputGroup: {
              fields: [
                {
                  formElement: {
                    attributes: {
                      id: "phone",
                      value: "call",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Phone call",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "text_message",
                      value: "text",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Text message",
                  },
                },
                {
                  formElement: {
                    attributes: {
                      id: "contact_email",
                      value: "email",
                      ...radioInputAttributes,
                    },
                    ...radioInputClasses,
                  },
                  label: {
                    classes: {
                      default: classes.radio.label.default,
                    },
                    text: "Email",
                  },
                },
              ],
              name: "preferred_method_of_contact",
              type: "radio",
              validationRules: {
                ...requiredOptionRule,
              },
            },
            tooltipAttributes: {
              ...tooltipAttributes.inputGroup,
            },
          },
        ],
      },
      imageSide: {
        description: {
          text: (
            <>
              Let us know the best way to contact you about your application.
              We'll respect your preference and keep communication simple and
              convenient.
            </>
          ),
        },
        image: {
          alt: "",
          gatsbyImageData: data.RingRing.childImageSharp.gatsbyImageData,
        },
      },
      stepName: "preferred_method_of_contact",
    },
  ]

  const navigateFunnelGaProps = isValid
    ? {
        "data-ga-trigger-event": "navigate_funnel",
        "data-ga-funnel_name": "Prequalify (Purchase)",
        "data-ga-lead_source": "Corp-Web-PrequalPurchase",
        "data-ga-current_step": funnelSteps[funnelData.currentStep].stepName,
      }
    : {}
  return (
    <>
      <FunnelStepScreen
        reactHookFormProps={reactHookFormProps}
        updateFunnelData={updateFunnelData}
        deleteSavedData={() =>
          typeof window !== "undefined" &&
          localStorage.removeItem(storedFunnelName)
        }
        gaProps={{ navigateFunnel: navigateFunnelGaProps }}
        {...funnelSteps[funnelData.currentStep]}
        formData={funnelData.formData}
        passedSteps={funnelData.passedSteps}
        currentStep={funnelData.currentStep}
        totalSteps={funnelSteps.length}
        startPath={props.location.state?.startPath}
        breakpoints={breakpoints}
      />
      <Modal isModalOpen={isModalOpen} closeModal={() => setIsModalOpen(false)}>
        <ConsentPopup
          updateConsentFieldValue={newValue =>
            setValue("terms", newValue, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            })
          }
          closeModal={() => setIsModalOpen(false)}
        />
      </Modal>
    </>
  )
}

/* -------------------------------- Meta Tags ------------------------------- */
export const Head = props => {
  return (
    <SEO
      title={`Mortgage Pre-Qualification | Fast Home Loan Approval`}
      description={`Complete our quick form to see how much home you can afford. No obligation, no hassle—just a fast pre-qualification process.`}
      ogImage={`https://images.ctfassets.net/n1no4gieqp7s/38yC4IrUlksSR5vLSYGonl/e88e48eade43939ee023a5ecb6a14759/purchase-prequalify-og.png`}
      slug={props.location.pathname.replace(/^\//, "").replace(/\/$/, "")}
    />
  )
}
